import { useState } from 'react'

import { revalidateLogic } from '@tanstack/react-form'
import * as z from 'zod'

import { useAppForm } from '@ui/components/forms'
import { Button } from '@ui/components/shadcn/ui/button'
import { FieldGroup } from '@ui/components/shadcn/ui/field'

import { useForgotPasswordMutation } from '@graphql/react-apollo'

import {
  useAuthAnalytics,
  useAuthScreenView,
} from '@web-app/components/auth/analytics'
import { validateEmail } from '@web-app/utils/validation'

const schema = z.object({
  email: z.string().trim().refine(validateEmail, {
    message: 'Enter a valid email address',
  }),
})

interface Props {
  initialEmail?: string
  onBackToSignIn: () => void
}

export function ForgotPasswordForm({ initialEmail = '', onBackToSignIn }: Props) {
  const [sentTo, setSentTo] = useState<string | null>(null)
  return sentTo ? (
    <ResetSent email={sentTo} onBackToSignIn={onBackToSignIn} />
  ) : (
    <RequestReset
      initialEmail={initialEmail}
      onSent={setSentTo}
      onBackToSignIn={onBackToSignIn}
    />
  )
}

function RequestReset({
  initialEmail,
  onSent,
  onBackToSignIn,
}: {
  initialEmail: string
  onSent: (email: string) => void
  onBackToSignIn: () => void
}) {
  // Browser-side on purpose: the backend rate-limits by connection IP.
  const [forgotPassword] = useForgotPasswordMutation()
  const { trackTap } = useAuthAnalytics()
  useAuthScreenView('forgotPassword')

  const form = useAppForm({
    defaultValues: { email: initialEmail },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: schema,
      onSubmitAsync: async ({ value }) => {
        trackTap('forgotPasswordNext')
        await forgotPassword({
          variables: { forgotPasswordInput: { email: value.email } },
        })
        // Same answer whether or not the account exists.
        onSent(value.email)
        return null
      },
    },
  })

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <p className="text-muted-foreground mb-6 text-center text-sm">
        Enter your email and we&apos;ll send you a link to reset your password.
      </p>
      <FieldGroup className="gap-2">
        <form.AppField name="email">
          {(field) => (
            <field.TextField
              label="Email"
              type="email"
              autoComplete="email"
              inputMode="email"
            />
          )}
        </form.AppField>
      </FieldGroup>
      <form.AppForm>
        <div className="mt-2 flex flex-col gap-2">
          <form.SubmitButton>Send reset link</form.SubmitButton>
          <form.FormError />
        </div>
      </form.AppForm>
      <p className="text-center text-sm">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm"
          onClick={onBackToSignIn}
        >
          Back to sign in
        </Button>
      </p>
    </form>
  )
}

function ResetSent({
  email,
  onBackToSignIn,
}: {
  email: string
  onBackToSignIn: () => void
}) {
  useAuthScreenView('resetSent')
  return (
    <div className="text-center text-sm">
      <p className="text-muted-foreground">
        If an account exists for{' '}
        <span className="text-foreground font-medium">{email}</span>, we sent
        a password reset link. Check your inbox.
      </p>
      <p className="mt-6">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm"
          onClick={onBackToSignIn}
        >
          Back to sign in
        </Button>
      </p>
    </div>
  )
}
